import React, { useState, useEffect } from 'react';
import { Card, Button, Avatar, SpinLoading } from 'antd-mobile';
import { useSearchParams } from 'react-router-dom';
import {
    CheckShieldOutline,
    HeartOutline,
    UserOutline
} from 'antd-mobile-icons';
import {
    BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip,
} from 'recharts';
import { theme } from '../theme';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://porddee-api.lungnuek.workers.dev/api';

interface InviteData {
    displayName: string;
    pictureUrl: string;
    mode: string;
    quitDays: number;
    todayCount: number;
    dailyLimit: number;
    nicotine: number;
    overLimit: number;
    hourlyData: { hour: number; count: number }[];
}

export const ShareDashboard: React.FC = () => {
    const [searchParams] = useSearchParams();
    const code = searchParams.get('code');

    // Legacy params support (fallback if no code)
    const legacyName = searchParams.get('n');
    const legacyUid = searchParams.get('uid');

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<InviteData | null>(null);
    const [cheered, setCheered] = useState(false);

    useEffect(() => {
        if (code) {
            fetchInviteData(code);
            registerViewer(code);
        } else if (legacyUid) {
            // Legacy fallback: build from old params
            fetchLegacyData(legacyUid);
        } else {
            setLoading(false);
            setError('ลิงก์คำเชิญไม่ถูกต้อง');
        }
    }, [code, legacyUid]);

    // Try to get viewer's LINE profile and register them
    const registerViewer = async (inviteCode: string) => {
        try {
            // @ts-ignore
            if (window.liff) {
                // @ts-ignore
                if (!window.liff.isLoggedIn()) {
                    // @ts-ignore
                    await window.liff.init({ liffId: '2009156565-sN0haQ3J' });
                }
                // @ts-ignore
                const profile = await window.liff.getProfile();
                if (profile?.displayName) {
                    await fetch(`${API_BASE_URL}/invite/${inviteCode}/viewer`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            viewerName: profile.displayName,
                            viewerPicture: profile.pictureUrl || '',
                        }),
                    });
                }
            }
        } catch (err) {
            // Not in LIFF or profile unavailable — that's OK
            console.log('Viewer registration skipped:', err);
        }
    };

    const fetchInviteData = async (inviteCode: string) => {
        try {
            const res = await fetch(`${API_BASE_URL}/invite/${inviteCode}`);
            if (!res.ok) {
                if (res.status === 404) {
                    setError('คำเชิญหมดอายุหรือไม่พบ');
                } else {
                    setError('เกิดข้อผิดพลาด');
                }
                setLoading(false);
                return;
            }
            const result = await res.json();
            setData(result);
        } catch (err) {
            console.error('Fetch invite error:', err);
            setError('ไม่สามารถโหลดข้อมูลได้');
        } finally {
            setLoading(false);
        }
    };

    const fetchLegacyData = async (userId: string) => {
        try {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const res = await fetch(`${API_BASE_URL}/logs/${userId}?startDate=${today.toISOString()}&limit=1000`);
            if (res.ok) {
                const result = await res.json();
                const logs = result.logs || [];
                const todayCount = logs.reduce((sum: number, l: any) => sum + l.count, 0);
                const hourlyData = Array(24).fill(0).map((_, i) => ({ hour: i, count: 0 }));
                logs.forEach((log: any) => {
                    const hour = new Date(log.loggedAt).getHours();
                    if (hour >= 0 && hour < 24) hourlyData[hour].count += log.count;
                });
                setData({
                    displayName: legacyName || 'เพื่อนของคุณ',
                    pictureUrl: searchParams.get('pic') || '',
                    mode: searchParams.get('m') || 'reduce',
                    quitDays: parseInt(searchParams.get('d') || '0'),
                    todayCount,
                    dailyLimit: 20,
                    nicotine: 0,
                    overLimit: Math.max(0, todayCount - 20),
                    hourlyData,
                });
            }
        } catch (err) {
            console.error('Legacy fetch error:', err);
            setError('ไม่สามารถโหลดข้อมูลได้');
        } finally {
            setLoading(false);
        }
    };

    const handleCheer = () => {
        setCheered(true);
        if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
    };

    // Loading state
    if (loading) {
        return (
            <div style={{
                minHeight: '100vh', display: 'flex', alignItems: 'center',
                justifyContent: 'center', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <SpinLoading color='primary' style={{ '--size': '48px' }} />
                    <p style={{ color: '#64748b', marginTop: 16 }}>กำลังโหลดข้อมูล...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !data) {
        return (
            <div style={{
                minHeight: '100vh', display: 'flex', alignItems: 'center',
                justifyContent: 'center', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                padding: 20
            }}>
                <Card style={{ borderRadius: 24, padding: 32, textAlign: 'center', maxWidth: 400 }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>😢</div>
                    <h2 style={{ color: '#0f172a', margin: '0 0 8px' }}>{error || 'ไม่พบข้อมูล'}</h2>
                    <p style={{ color: '#64748b', margin: 0 }}>ลิงก์คำเชิญอาจหมดอายุแล้ว</p>
                </Card>
            </div>
        );
    }

    return (
        <div className="page" style={{
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '40px 20px'
        }}>
            {/* Header / Avatar */}
            <div style={{ marginBottom: 24, textAlign: 'center' }}>
                <div style={{
                    width: 100, height: 100, borderRadius: '50%',
                    background: 'white', margin: '0 auto 16px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 8px 24px rgba(${theme.primaryRgb}, 0.15)`,
                    padding: 4
                }}>
                    <Avatar
                        src={data.pictureUrl}
                        style={{ '--size': '100px', '--border-radius': '50%' }}
                        fallback={<UserOutline style={{ fontSize: 48, color: '#94a3b8' }} />}
                    />
                </div>
                <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: '0 0 8px' }}>
                    {data.displayName} กำลัง{data.mode === 'quit' ? 'เลิกบุหรี่' : 'ลดการสูบ'}!
                </h1>
                <p style={{ color: '#64748b', margin: 0 }}>
                    มาช่วยส่งกำลังใจให้เขากันเถอะ ❤️
                </p>
            </div>

            {/* Stats Overview */}
            <div style={{ width: '100%', maxWidth: 400, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 24 }}>
                <Card style={{ borderRadius: 20, textAlign: 'center', background: '#ffffff' }}>
                    <div style={{ fontSize: 28, marginBottom: 4 }}>🔥</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>{data.todayCount}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>สูบวันนี้</div>
                </Card>
                <Card style={{ borderRadius: 20, textAlign: 'center', background: '#ffffff' }}>
                    <div style={{ fontSize: 28, marginBottom: 4 }}>🎯</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: theme.primary }}>{data.dailyLimit}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>เป้าหมาย</div>
                </Card>
                <Card style={{ borderRadius: 20, textAlign: 'center', background: '#ffffff' }}>
                    <div style={{ fontSize: 28, marginBottom: 4 }}>⚠️</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: data.overLimit > 0 ? '#ef4444' : '#10b981' }}>{data.overLimit > 0 ? `+${data.overLimit}` : '✓'}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{data.overLimit > 0 ? 'เกินเป้า' : 'ไม่เกิน'}</div>
                </Card>
            </div>

            {/* Chart Card */}
            <Card style={{
                width: '100%', maxWidth: 400, borderRadius: 24,
                marginBottom: 24, padding: 20, background: '#fff',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: '0 0 16px' }}>
                    📊 สถิติการสูบวันนี้
                </h2>
                <div style={{ height: 200, width: '100%', minHeight: 200 }}>
                    {data.hourlyData && data.hourlyData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.hourlyData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                                <XAxis
                                    dataKey="hour"
                                    axisLine={true} tickLine={false}
                                    tick={{ fontSize: 10, fill: '#94a3b8' }}
                                    interval={3}
                                />
                                <YAxis axisLine={false} tickLine={true} tick={{ fontSize: 10, fill: '#94a3b8' }} domain={[0, 'auto']} />
                                <Tooltip
                                    cursor={{ fill: '#f1f5f9' }}
                                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    formatter={(value: any) => [`${value} ครั้ง`, 'จำนวนสูบ']}
                                />
                                <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={8} fill={theme.primary} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}>ไม่มีข้อมูล</div>
                    )}
                </div>
            </Card>

            {/* Big Cheer Button */}
            <Card style={{
                width: '100%', maxWidth: 400, borderRadius: 24,
                background: 'white', padding: 24, textAlign: 'center',
                boxShadow: '0 12px 32px rgba(16, 185, 129, 0.1)'
            }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 16px', color: '#0f172a' }}>
                    ส่งกำลังใจให้ {data.displayName}
                </h3>

                {!cheered ? (
                    <Button
                        block
                        size='large'
                        onClick={handleCheer}
                        style={{
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            border: 'none', borderRadius: 16,
                            color: 'white', fontWeight: 700, fontSize: 18,
                            height: 60,
                            boxShadow: '0 8px 20px rgba(239, 68, 68, 0.3)'
                        }}
                    >
                        <HeartOutline style={{ marginRight: 8, fontSize: 24 }} />
                        เก่งมาก! สู้ๆ นะ
                    </Button>
                ) : (
                    <div style={{ padding: '20px 0', animation: 'fadeIn 0.5s' }}>
                        <div style={{ fontSize: 40, marginBottom: 8 }}>🥰</div>
                        <div style={{ fontWeight: 700, color: '#10b981', fontSize: 18 }}>ส่งกำลังใจเรียบร้อย!</div>
                        <div style={{ color: '#64748b', fontSize: 14 }}>ขอบคุณที่เป็นส่วนหนึ่งในความสำเร็จ</div>
                    </div>
                )}
            </Card>

            <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: 8, opacity: 0.6 }}>
                <CheckShieldOutline color={theme.primary} />
                <span style={{ fontSize: 12, color: '#475569' }}>Verified by Porddee</span>
            </div>
        </div>
    );
};
